var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CreateSubModelMessage } from "flavr-common";
import { busService } from "@modules/bus";
import { Model } from "./Model";
import { modelPool } from "./ModelPool";
import { modelToAbsolutePath } from "./modelPath/modelPathUtils";
import { ModelPath } from "./modelPath/ModelPath";
/// More robust method to get the model name ///
const knownModelNames = new Map();
/**
 * Returns the name of the model that was specified or returns the default name if the model doesn't exist.
 * If you need more detailed info for printing (like the name of deleted models) then use `determineModelNameForPrinting`
 * instead.
 */
export const getModelNameByUID = (uid, defaultName = "unknown") => {
    var _a, _b;
    return (_b = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : defaultName;
};
/**
 * Tries to determine the name of a model for printing. This works in certain cases even when the model has been deleted.
 * However, it should *ONLY* be used for printing the name because it also formats the name depending on certain conditions.
 * If you just want the name of model by uid, then consider using `getModelNameByUID` instead.
 *
 * The name is formated as follows:
 *
 * a) If the model exists then the name will be displayed as `ABSOLUTE_MODEL_PATH::UID`
 *
 * b) If the model did exist before but doesn't exist anymore it will be displayed as `<deleted:ABSOLUTE_MODEL_PATH>`
 *
 * c) If the model is totally unknown, it will be displayed as `<unknown:UID>`
 *
 * d) If the modelUID provided is undefined `<undefined>` is returned
 */
export const determineModelNameForPrinting = (modelOrUID, printProps) => {
    var _a;
    if (modelOrUID === undefined)
        return `<undefined>`;
    const printModelPath = (_a = printProps === null || printProps === void 0 ? void 0 : printProps.printModelPath) !== null && _a !== void 0 ? _a : true;
    const model = (modelOrUID instanceof Model) ? modelOrUID : modelPool.getModel(modelOrUID);
    if (model !== undefined) {
        return printModelPath
            ? `${modelToAbsolutePath(model)}::${model.getUID()}`
            : `${model.getName()}::${model.getUID()}`;
    }
    const uid = (modelOrUID instanceof Model) ? modelOrUID.getUID() : modelOrUID;
    const knownName = knownModelNames.get(uid);
    if (knownName !== undefined) {
        const name = printModelPath ? knownName : new ModelPath(knownName).basename();
        return `<deleted:${name}::${uid}>`;
    }
    return `<unknown:${uid}>`;
};
busService.connection("DebugDetermineModelName").subscribeByType(CreateSubModelMessage, (msg) => {
    waitForModel(msg.uid, (model) => {
        knownModelNames.set(model.getUID(), `${modelToAbsolutePath(model)}::${msg.uid}`);
    });
});
/// Wait for model functions ///
/**
 * Waits until a model with the specified uid was created and calls the provided callback with that model.
 * If the model already existed, then the callback is called immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified uid was created after the timeout expired.
 */
export const waitForModel = (uid, callback, timeout) => {
    if (modelPool.hasModel(uid)) {
        callback(modelPool.getModelOrThrow(uid));
        return;
    }
    waitForModelWithPredicate(`${uid}`, (model) => model.getUID() === uid, callback, timeout);
};
/**
 * Waits asynchronously until a model with the specified uid was created and returns that model.
 * If the model already existed, then the model is returned immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified uid was created after the timeout expired.
 */
export const waitForModelAsync = (uid, timeout) => __awaiter(void 0, void 0, void 0, function* () {
    const model = modelPool.getModel(uid);
    if (model !== undefined)
        return model;
    return yield waitForModelWithPredicateAsync(`${uid}`, (model) => model.getUID() === uid, timeout);
});
/**
 * Waits until a model with the specified name was created and calls the provided callback with that model.
 * If the model already existed, then the callback is called immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified name was created after the timeout expired.
 *
 * *Attention*: More than one model could have the same name, so be careful when using this function
 */
export const waitForModelWithName = (name, callback, timeout) => {
    const model = findModelByName(name);
    if (model !== undefined) {
        callback(model);
        return;
    }
    waitForModelWithPredicate(name, (model) => model.getName() === name, callback, timeout);
};
/**
 * Waits asynchronously until a model with the specified name was created and returns that model.
 * If the model already existed, then the model is returned immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified name was created after the timeout expired.
 *
 * *Attention*: More than one model could have the same name, so be careful when using this function
 */
export const waitForModelWithNameAsync = (name, timeout) => __awaiter(void 0, void 0, void 0, function* () {
    const model = findModelByName(name);
    if (model !== undefined)
        return model;
    return yield waitForModelWithPredicateAsync(name, (model) => model.getName() === name, timeout);
});
/// Find model functions ///
/**
 * Finds and returns a model by the specified name. However, take note that there could be more than one model with the
 * same name. In this case the first model that was found is returned. If you want to get all models with the specified
 * name consider using `modelPool.findModelsWithName(name)` instead
 */
export const findModelByName = (name) => {
    const modelsWithName = modelPool.findModelsWithName(name);
    if (modelsWithName.length > 0) {
        if (modelsWithName.length > 1)
            console.warn(`There are multiple models with the name "${name}", we take the first model`, modelsWithName);
        return modelsWithName[0];
    }
    return undefined;
};
/// Private helper methods ///
/**
 * Waits until a model with the specified predicate was created and calls the provided callback with that model.
 * If the model already existed, then the callback is called immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified predicate was created after the timeout expired.
 *
 * If possible, consider using `waitForModel(uid, callback)`, `waitForModelWithName(name, callback)` or
 * `waitForModelWithPath(absolutePath, callback)` instead
 */
export const waitForModelWithPredicate = (tag, predicate, callback, timeout) => {
    if (timeout !== undefined) {
        const timeoutHandle = setTimeout(() => {
            throw new Error(`Model "${tag}" wasn't received in the specified timeout duration of ${timeout}ms`);
        }, timeout);
        const unsubscribe = modelPool.setOnModelCreatedHandler((model) => {
            if (!predicate(model))
                return;
            clearTimeout(timeoutHandle);
            unsubscribe();
            callback(model);
        });
    }
    else {
        const unsubscribe = modelPool.setOnModelCreatedHandler((model) => {
            if (!predicate(model))
                return;
            unsubscribe();
            callback(model);
        });
    }
};
/**
 * Waits asynchronously until a model with the specified predicate was created and returns that model.
 * If the model already existed, then the model is returned immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified predicate was created after the timeout expired.
 *
 * If possible, consider using `waitForModelAsync(uid)`, `waitForModelWithNameAsync(name)` or
 * `waitForModelWithPathAsync(absolutePath)` instead
 */
export const waitForModelWithPredicateAsync = (tag, predicate, timeout) => __awaiter(void 0, void 0, void 0, function* () {
    return yield new Promise((resolve, reject) => {
        if (timeout !== undefined) {
            const timeoutHandle = setTimeout(() => {
                reject(new Error(`Model "${tag}" wasn't received in the specified timeout duration of ${timeout}ms`));
            }, timeout);
            const unsubscribe = modelPool.setOnModelCreatedHandler((model) => {
                if (!predicate(model))
                    return;
                clearTimeout(timeoutHandle);
                unsubscribe();
                resolve(model);
            });
        }
        else {
            const unsubscribe = modelPool.setOnModelCreatedHandler((model) => {
                if (!predicate(model))
                    return;
                unsubscribe();
                resolve(model);
            });
        }
    });
});
